<template>
  <div class="card">
    <h3>Ortaklık Yapısı</h3>
    <div class="chart-container">
      <div class="pie-chart-container">
        <svg viewBox="0 0 200 200" class="pie-chart">
          <g transform="translate(100, 100)">
            <template v-for="(slice, index) in pieSlices" :key="index">
              <path
                :d="slice.path"
                :fill="slice.color"
                class="pie-slice"
                @mouseover="activeSlice = index"
                @mouseout="activeSlice = null"
                :class="{ active: activeSlice === index }"
              />
            </template>
            <circle r="40" fill="var(--card-bg)" />
          </g>
        </svg>
      </div>

      <div class="legend-container">
        <h3>Ortaklık Dağılımı</h3>
        <div class="legend-items">
          <div
            v-for="(ortak, index) in filteredOrtaklar"
            :key="index"
            class="legend-item"
            :class="{ active: activeSlice === index }"
            @mouseover="activeSlice = index"
            @mouseout="activeSlice = null"
          >
            <div
              class="legend-color"
              :style="{ backgroundColor: colors[index] }"
            ></div>
            <div class="legend-text">
              <div
                class="legend-label"
                :title="ortak['Ortağın Adı-Soyadı/Ticaret Unvanı']"
              >
                {{
                  truncateText(ortak["Ortağın Adı-Soyadı/Ticaret Unvanı"], 30)
                }}
              </div>
              <div class="legend-value">
                {{ ortak["Sermayedeki Payı (%)"] }}% ({{
                  formatCurrency(ortak["Sermayedeki Payı (TL)"])
                }})
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="finance-table-container">
      <table class="finance-table">
        <thead>
          <tr>
            <th>Ortak</th>
            <th>Oy Hakkı (%)</th>
            <th>Sermaye Payı (%)</th>
            <th>Sermaye Payı (TL)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(ortak, index) in sirketBilgileri" :key="index">
            <td>{{ ortak["Ortağın Adı-Soyadı/Ticaret Unvanı"] }}</td>
            <td>{{ ortak["Oy Hakkı Oranı (%)"] }}</td>
            <td>{{ ortak["Sermayedeki Payı (%)"] }}</td>
            <td>{{ formatCurrency(ortak["Sermayedeki Payı (TL)"]) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { formatCurrency } from "@/utils/formatHelpers";

export default {
  props: {
    sirketBilgileri: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      colors: [
        "#10b981", // Yeşil
        "#6366f1", // Mavi
        "#f59e0b", // Turuncu
        "#ef4444", // Kırmızı
        "#8b5cf6", // Mor
        "#ec4899", // Pembe
        "#3b82f6", // Lila
        "#facc15", // Sarı
        "#5b21b6", // Fushya
        "#7c3aed", // Lavanta
        "#122C9FFF", // Koyu Mavi
      ],
      activeSlice: null,
    };
  },
  computed: {
    // TOPLAM satırını çıkararak sadece gerçek ortakları filtrele
    filteredOrtaklar() {
      return this.sirketBilgileri.filter(
        (ortak) => ortak["Ortağın Adı-Soyadı/Ticaret Unvanı"] !== "TOPLAM"
      );
    },
    // Pasta dilimlerini hesapla
    pieSlices() {
      const filteredData = this.filteredOrtaklar;
      const slices = [];
      let startAngle = 0;

      for (let i = 0; i < filteredData.length; i++) {
        const percentage = parseFloat(
          filteredData[i]["Sermayedeki Payı (%)"].replace(",", ".")
        );
        const angle = (percentage / 100) * 360;
        const endAngle = startAngle + angle;

        // SVG için ark yolu oluştur
        const x1 = 80 * Math.cos((startAngle * Math.PI) / 180);
        const y1 = 80 * Math.sin((startAngle * Math.PI) / 180);
        const x2 = 80 * Math.cos((endAngle * Math.PI) / 180);
        const y2 = 80 * Math.sin((endAngle * Math.PI) / 180);

        // Büyük ark bayrağı (large-arc-flag)
        const largeArcFlag = angle > 180 ? 1 : 0;

        // SVG path oluştur
        const path = `M ${x1} ${y1} A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2} L 0 0 Z`;

        slices.push({
          path,
          color: this.colors[i % this.colors.length],
        });

        startAngle = endAngle;
      }

      return slices;
    },
  },
  methods: {
    formatCurrency(value) {
      return formatCurrency(value);
    },
    truncateText(text, maxLength) {
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;
    },
  },
};
</script>

<style scoped>
:root {
  --success-color: #10b981;
  --danger-color: #ef4444;
  --card-bg: #1e293b;
  --card-dark: #0f172a;
  --hover-bg: #334155;
}

.card {
  background-color: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

h2 {
  color: var(--text-secondary);
  margin-bottom: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.chart-container {
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
}

.pie-chart-container {
  flex: 0 0 200px;
  position: relative;
}

.pie-chart {
  width: 100%;
  height: auto;
}

.pie-slice {
  transition: transform 0.3s ease, filter 0.3s ease;
  transform-origin: center;
}

.pie-slice.active {
  transform: scale(1.05);
  filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
}

.legend-container {
  flex: 1;
  min-width: 0;
}

.legend-items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.75rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: var(--card-dark);
  transition: all 0.2s ease;
  width: 100%;
}

.legend-item:hover,
.legend-item.active {
  background-color: var(--hover-bg);
  transform: translateX(5px);
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
}

.legend-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
}

.legend-label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.legend-value {
  font-size: 13px;
  color: var(--text-muted);
}

.finance-table-container {
  overflow-x: auto;
}

.finance-table {
  width: 100%;
  border-collapse: collapse;
  background-color: var(--card-dark);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  margin-bottom: 1rem;
}

.finance-table th,
.finance-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
}

.finance-table th {
  font-weight: 600;
  color: var(--text-primary);
  background-color: var(--card-dark);
}

.finance-table td {
  color: var(--text-secondary);
}

.finance-table th:not(:first-child),
.finance-table td:not(:first-child) {
  text-align: right;
}

.finance-table tr:last-child td {
  border-bottom: none;
}

.finance-table tbody tr:nth-child(odd) {
  background-color: rgba(255, 255, 255, 0.02);
}

.finance-table tbody tr:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.05);
}

.finance-table tbody tr:hover {
  background-color: rgba(55, 175, 255, 0.1);
}

@media (max-width: 992px) {
  .chart-container {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .pie-chart-container {
    flex: 0 0 auto;
    width: 200px;
  }

  .legend-items {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
}

@media (max-width: 768px) {
  .card {
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .chart-container {
    gap: 1rem;
  }

  .pie-chart-container {
    width: 160px;
  }

  h3 {
    font-size: 15px;
    margin-bottom: 0.75rem;
  }

  .legend-items {
    grid-template-columns: 1fr;
  }

  .legend-item {
    padding: 0.5rem;
  }

  .legend-label {
    font-size: 12px;
  }

  .legend-value {
    font-size: 12px;
  }

  .finance-table th,
  .finance-table td {
    padding: 0.75rem;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .pie-chart-container {
    width: 140px;
  }

  .legend-items {
    grid-template-columns: 1fr;
  }

  .legend-item {
    padding: 0.5rem;
  }

  .legend-label {
    font-size: 11px;
  }

  .legend-value {
    font-size: 11px;
  }

  .finance-table th,
  .finance-table td {
    padding: 0.5rem;
    font-size: 11px;
  }
}

@media (max-width: 360px) {
  .card {
    padding: 0.75rem;
  }

  .pie-chart-container {
    width: 120px;
  }

  h3 {
    font-size: 14px;
  }

  .legend-label {
    font-size: 11px;
  }

  .legend-value {
    font-size: 10px;
  }

  .finance-table th,
  .finance-table td {
    padding: 0.5rem 0.3rem;
    font-size: 10px;
  }
}
</style>
